import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Chip,
  Box,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useGlobalConfig } from 'context/GlobalContext';
import { PilotApis, PendingUatConfig } from 'apis/pilotAPI';

/** Build human-readable level from configLevel + productType + fuelType + clusterReference. */
function getDisplayLevel(config: {
  configLevel: string;
  productType?: string | null;
  fuelType?: string | null;
  clusterReference?: string | null;
}): string {
  const level = (config.configLevel ?? '').toString().trim().toLowerCase();
  const pt = (config.productType ?? '').toString().trim();
  const ft = (config.fuelType ?? '').toString().trim();
  const cr = (config.clusterReference ?? '').toString().trim();
  const prefix = level === 'cluster' ? 'CLUSTER' : 'PILOT';

  if (level === 'cluster') {
    return cr ? `${prefix}.${cr}` : prefix;
  }
  if (level === 'fuel_type') {
    const parts = [pt, ft].filter(Boolean).join('.');
    return parts ? `${prefix}.${parts}` : prefix;
  }
  if (level === 'product_type') {
    return pt ? `${prefix}.${pt}` : prefix;
  }
  if (level === 'pilot') return 'PILOT';
  if (level === 'global' || level === 'project') return level.toUpperCase();
  return prefix;
}

interface PushUpdatesDialogProps {
  open: boolean;
  onClose: () => void;
  environmentName: string;
  projectId: string;
  onPushed?: (summary: any) => void;
}

export default function PushUpdatesDialog(props: PushUpdatesDialogProps) {
  const { open, onClose, environmentName, projectId, onPushed } = props;
  const { rc } = useGlobalConfig();

  const [pending, setPending] = useState<PendingUatConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!open || !projectId) return;
      setIsLoading(true);
      try {
        const resp = await PilotApis.getPendingUatConfigs(rc, projectId);
        setPending(resp.success && resp.data ? resp.data : []);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [open, projectId, rc]);

  const [localRows, setLocalRows] = useState<Array<{ id: string; configKey: string; configType: string; configLevel: string; displayLevel: string; originalValue: string; currentValue: string }>>([]);

  useEffect(() => {
    if (open) {
      const source = Array.isArray(pending) ? pending : [];
      setLocalRows(source.map(p => ({
        id: p.id,
        configKey: p.configKey,
        configType: p.configType ?? '',
        configLevel: p.configLevel,
        displayLevel: getDisplayLevel({ configLevel: p.configLevel, productType: p.productType ?? null, fuelType: p.fuelType ?? null, clusterReference: p.clusterReference ?? null }),
        originalValue: String(p.pingpong_value ?? ''),
        currentValue: String(p.db_value ?? '')
      })));
    }
  }, [open, pending]);

  const hasRows = localRows.length > 0;

  const handleRemove = async (id: string) => {
    if (!projectId) return;
    const source = Array.isArray(pending) ? pending : [];
    const src = source.find(p => p.id === id);
    if (!src) {
      // Remove locally if we can't find the source
      setLocalRows(prev => prev.filter(r => r.id !== id));
      return;
    }
    setIsApplying(true);
    try {
      const payload = [{
        project_id: projectId,
        config_key: src.configKey,
        config_type: src.configType || 'CUSTOM',
        config_value: String(src.pingpong_value ?? ''), // revert to old value
        config_level: src.configLevel || 'pilot',
        config_documentation: src.configDocumentation || '',
        config_regex: '.*',
        config_data_type: src.configDataType || '',
        display_name: src.configKey,
        features: (src as any).component || 'ingestion',
        editable: true,
        product_type: src.productType || '',
        fuel_type: src.fuelType || '',
        is_required: true,
        revert_to_original: true,
        ...(src.configLevel === 'cluster' && src.clusterId ? { cluster_id: src.clusterId } : {}),
      }];
      const saveResp = await PilotApis.saveProjectConfigs(rc, payload as any);
      if (saveResp.success) {
        setLocalRows(prev => prev.filter(r => r.id !== id));
        setPending(prev => (Array.isArray(prev) ? prev.filter(p => p.id !== id) : prev));
      }
    } finally {
      setIsApplying(false);
    }
  };
  const handleChange = (id: string, value: string) => setLocalRows(prev => prev.map(r => r.id === id ? { ...r, currentValue: value } : r));

  const handleApply = async () => {
    if (!projectId) return;
    setIsApplying(true);
    try {
      // Build Save All payload: edited rows use currentValue; removed rows revert to originalValue
      const keptIds = new Set(localRows.map(r => r.id));
      const source = Array.isArray(pending) ? pending : [];
      const removed = source.filter(p => !keptIds.has(p.id));

      const payload = [
        // kept edits
        ...localRows.map(r => {
          const src = source.find(p => p.id === r.id)!;
          return {
            project_id: projectId,
            config_key: src.configKey,
            config_type: src.configType || 'CUSTOM',
            config_value: String(r.currentValue ?? ''),
            config_level: src.configLevel || 'pilot',
            config_documentation: src.configDocumentation || '',
            config_regex: '.*',
            config_data_type: src.configDataType || '',
            display_name: src.configKey,
            features: (src as any).component || 'ingestion',
            editable: true,
            product_type: src.productType || '',
            fuel_type: src.fuelType || '',
            is_required: true
          };
        }),
        // removed -> revert to original
        ...removed.map(src => ({
          project_id: projectId,
          config_key: src.configKey,
          config_type: src.configType || 'CUSTOM',
          config_value: String(src.pingpong_value ?? ''),
          config_level: src.configLevel || 'pilot',
          config_documentation: src.configDocumentation || '',
          config_regex: '.*',
          config_data_type: src.configDataType || '',
          display_name: src.configKey,
          features: (src as any).component || 'ingestion',
          editable: true,
          product_type: src.productType || '',
          is_required: true,
          fuel_type: src.fuelType || '',
          revert_to_original: true,
          ...(src.configLevel === 'cluster' && src.clusterId ? { cluster_id: src.clusterId } : {}),
        }))
      ];

      if (payload.length > 0) {
        const saveResp = await PilotApis.saveProjectConfigs(rc, payload as any);
        if (!saveResp.success) {
          return;
        }
      }

      const pushResp = await PilotApis.pushConfigsToUat(rc, projectId);
      if (pushResp.success) {
        onPushed?.(pushResp.data);
      }
      onClose();
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          marginLeft: 'var(--drawer-width, 270px)',
          maxWidth: 'calc(100vw - var(--drawer-width, 270px))'
        }
      }}
    >
      <DialogTitle>Push Configuration Updates</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2, color: 'text.secondary' }}>
          Review pending configuration changes for <b>{environmentName}</b>. Remove any entries to revert them to their original values before pushing.
        </Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: '5%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '21%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Config Type</TableCell>
                <TableCell>Config Key</TableCell>
                <TableCell>Original Value</TableCell>
                <TableCell>New Value</TableCell>
                <TableCell>Level</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hasRows ? (
                localRows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Tooltip title="Remove from push (revert to original)">
                        <span>
                          <IconButton size="small" onClick={() => handleRemove(row.id)} disabled={isApplying}>
                            <DeleteOutlineIcon fontSize="small" color="error" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', overflow: 'hidden', maxWidth: 0 }}>
                      <Tooltip title={row.configType} placement="top" arrow enterDelay={300}>
                        <Box component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.configType}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', overflow: 'hidden', maxWidth: 0 }}>
                      <Tooltip title={row.configKey} placement="top" arrow enterDelay={300}>
                        <Box component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.configKey}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', minWidth: 160 }}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={12}
                        value={row.originalValue}
                        variant="outlined"
                        InputProps={{
                          readOnly: true,
                          sx: { fontFamily: 'monospace', fontSize: '0.8125rem', backgroundColor: 'action.hover' }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', minWidth: 160 }}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={12}
                        value={row.currentValue}
                        onChange={(e) => handleChange(row.id, e.target.value)}
                        variant="outlined"
                        InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8125rem' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ overflow: 'hidden', maxWidth: 0 }}>
                      <Tooltip title={row.displayLevel} placement="top" arrow enterDelay={300}>
                        <Box sx={{ display: 'block', minWidth: 0, overflow: 'hidden' }}>
                          <Chip size="small" label={row.displayLevel} variant="outlined" sx={{ fontFamily: 'inherit', maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                    No pending configuration updates.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isApplying}>Cancel</Button>
        <Button onClick={handleApply} disabled={!hasRows || isApplying || isLoading} variant="contained" startIcon={isApplying ? <CircularProgress size={16} /> : undefined}>
          {isApplying ? 'Processing…' : 'Update Configs'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 